// Página AdminProfitSettings - Configuración de ganancia general
// Permite configurar el porcentaje de ganancia general del sistema

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getProfitSettings, updateProfitSettings } from '../../services/profit.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminProfitSettings.css'

/**
 * Página AdminProfitSettings
 * Configuración de ganancia general del sistema
 */
function AdminProfitSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  
  const [formData, setFormData] = useState({
    defaultProfitPercentage: ''
  })

  // Cargar configuración actual
  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const settings = await getProfitSettings()
      setFormData({
        defaultProfitPercentage: settings.defaultProfitPercentage?.toString() || '30'
      })
    } catch (error) {
      console.error('Error cargando configuración:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar la configuración de ganancia'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validación
    const percentage = parseFloat(formData.defaultProfitPercentage)
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      setNotification({
        type: 'error',
        message: 'El porcentaje debe ser un número entre 0 y 100'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      await updateProfitSettings({
        defaultProfitPercentage: percentage
      })
      
      setNotification({
        type: 'success',
        message: 'Configuración de ganancia actualizada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error guardando configuración:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (loading) {
    return (
      <div className="admin-profit-settings-page">
        <div className="loading-state">
          <p>Cargando configuración...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-profit-settings-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="profit-settings-header">
        <div>
          <Link to="/admin/configuraciones">
            <button className="back-button">← Volver</button>
          </Link>
          <h1>Configuración de Ganancia General</h1>
          <p className="profit-settings-subtitle">
            Establece el porcentaje de ganancia por defecto para todas las categorías que no tengan un porcentaje específico configurado
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="profit-settings-form-section">
        <form onSubmit={handleSubmit} className="profit-settings-form">
          <div className="form-group">
            <label htmlFor="defaultProfitPercentage">
              Porcentaje de Ganancia General <span className="required">*</span>
            </label>
            <div className="input-with-suffix">
              <input
                type="number"
                id="defaultProfitPercentage"
                name="defaultProfitPercentage"
                value={formData.defaultProfitPercentage}
                onChange={handleChange}
                placeholder="30"
                min="0"
                max="100"
                step="0.01"
                required
                disabled={saving}
                className="form-input"
              />
              <span className="input-suffix">%</span>
            </div>
            <small className="form-hint">
              Este porcentaje se aplicará a todas las categorías que no tengan un porcentaje de ganancia específico configurado.
              Las categorías con porcentaje configurado siempre tendrán prioridad sobre este valor general.
            </small>
          </div>

          {/* Información adicional */}
          <div className="info-box">
            <h3>📊 Cómo funciona:</h3>
            <ul>
              <li>
                <strong>Prioridad de Categoría:</strong> Si una categoría tiene un % de ganancia configurado, 
                ese valor se usará para todos los productos de esa categoría.
              </li>
              <li>
                <strong>Ganancia General:</strong> Si una categoría no tiene % configurado, se usará este 
                porcentaje general.
              </li>
              <li>
                <strong>Uso en Compras:</strong> Este porcentaje se utilizará al cargar pedidos de compra 
                para calcular el precio de venta sugerido basado en el costo de compra.
              </li>
            </ul>
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminProfitSettings

